import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {MapPin, Train, Calendar, DollarSign, Utensils, Hotel, Camera} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function Japan() {
  const scrollY = useParallax();
  const { ref: overviewRef, isVisible: overviewVisible } = useScrollAnimation(0.2);
  const { ref: citiesRef, isVisible: citiesVisible } = useScrollAnimation(0.2);
  const { ref: attractionsRef, isVisible: attractionsVisible } = useScrollAnimation(0.2);

  const cities = [
    { name: 'Tokyo', slug: '/destinations/japan/tokyo', image: 'https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg?auto=compress&cs=tinysrgb&w=800' },
    { name: 'Osaka', slug: '/destinations/japan/osaka', image: 'https://images.pexels.com/photos/4151484/pexels-photo-4151484.jpeg?auto=compress&cs=tinysrgb&w=800' },
    { name: 'Kyoto', slug: '/destinations/japan/kyoto', image: 'https://images.pexels.com/photos/402028/pexels-photo-402028.jpeg?auto=compress&cs=tinysrgb&w=800' },
    { name: 'Theme Parks', slug: '/destinations/japan/theme-parks', image: 'https://images.pexels.com/photos/2166559/pexels-photo-2166559.jpeg?auto=compress&cs=tinysrgb&w=800' }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section with Parallax */}
      <div className="relative h-[500px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/161401/fushimi-inari-taisha-shrine-kyoto-japan-temple-161401.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Japan"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div 
              className="max-w-2xl"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Japan</h1>
              <p className="text-xl text-white/90 mb-6">
                Where ancient traditions meet cutting-edge innovation. Experience the perfect blend of temples, technology, cuisine, and culture.
              </p>
              <div className="flex flex-wrap gap-4">
                <motion.div 
                  className="flex items-center text-white bg-white/20 backdrop-blur-sm rounded-full px-4 py-2"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.3)' }}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  <span>Best: Mar-May, Sep-Nov</span>
                </motion.div>
                <motion.div 
                  className="flex items-center text-white bg-white/20 backdrop-blur-sm rounded-full px-4 py-2"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.3)' }}
                >
                  <DollarSign className="w-5 h-5 mr-2" />
                  <span>$$$ - $$$$ per day</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Overview Section with Scroll Animation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div ref={overviewRef} className="grid md:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={overviewVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Why Visit Japan?</h2>
            <p className="text-gray-600 mb-4">
              Japan offers an unparalleled travel experience where bullet trains whisk you from ultra-modern cities to serene mountain temples in hours. Sample world-class sushi in Tokyo, soak in onsen hot springs, witness cherry blossoms, and explore ancient castles.
            </p>
            <p className="text-gray-600">
              From the neon-lit streets of Shibuya to the peaceful bamboo groves of Kyoto, Japan seamlessly blends 1,000 years of history with tomorrow's technology.
            </p>
          </motion.div>

          <motion.div 
            className="bg-white rounded-xl p-6 shadow-lg"
            initial={{ opacity: 0, x: 50 }}
            animate={overviewVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
          >
            <h3 className="text-xl font-bold text-[#0A2540] mb-4">Essential Information</h3>
            <div className="space-y-3">
              {[
                { icon: Train, color: '#4A90E2', title: 'JR Pass', detail: '7-day pass: ~¥29,650 ($200) – unlimited trains' },
                { icon: Utensils, color: '#FF6B35', title: 'Food Budget', detail: '¥2,000-5,000 per day ($15-35)' },
                { icon: Hotel, color: '#F7B32B', title: 'Accommodation', detail: 'Budget: ¥3,000-6,000 | Mid: ¥8,000-15,000' },
                { icon: MapPin, color: '#0A2540', title: 'Visa', detail: '90-day visa-free for most countries' }
              ].map((item, idx) => (
                <motion.div 
                  key={idx}
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={overviewVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
                >
                  <item.icon className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" style={{ color: item.color }} />
                  <div>
                    <p className="font-semibold text-gray-800">{item.title}</p>
                    <p className="text-sm text-gray-600">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Top Attractions with Staggered Animation */}
        <div ref={attractionsRef} className="mb-16">
          <motion.h2 
            className="text-3xl font-bold text-[#0A2540] mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={attractionsVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            Must-Visit Attractions
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Tokyo Skytree', icon: Camera, color: '#4A90E2' },
              { name: 'Fushimi Inari Shrine', icon: MapPin, color: '#FF6B35' },
              { name: 'Universal Studios Japan', icon: Camera, color: '#F7B32B' },
              { name: 'Mount Fuji', icon: MapPin, color: '#0A2540' }
            ].map((attraction, idx) => (
              <motion.div
                key={idx}
                className="bg-white rounded-xl p-6 shadow-md text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={attractionsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -10, boxShadow: '0 15px 30px rgba(0,0,0,0.15)' }}
              >
                <attraction.icon className="w-12 h-12 mx-auto mb-3" style={{ color: attraction.color }} />
                <h4 className="font-semibold text-gray-800">{attraction.name}</h4>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Cities Grid with Parallax Cards */}
        <div ref={citiesRef}>
          <motion.h2 
            className="text-3xl font-bold text-[#0A2540] mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={citiesVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            Explore Cities & Regions
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            {cities.map((city, idx) => (
              <motion.div
                key={city.slug}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={citiesVisible ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                whileHover={{ scale: 1.03 }}
              >
                <Link to={city.slug} className="group block">
                  <div className="relative h-64 rounded-xl overflow-hidden shadow-lg">
                    <motion.img
                      src={city.image}
                      alt={city.name}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.4 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 to-transparent" />
                    <motion.div 
                      className="absolute bottom-6 left-6 right-6"
                      whileHover={{ y: -5 }}
                    >
                      <h3 className="text-2xl font-bold text-white mb-2">{city.name}</h3>
                      <div className="inline-flex items-center text-white bg-[#FF6B35] px-4 py-2 rounded-full text-sm font-medium">
                        Explore {city.name} →
                      </div>
                    </motion.div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
